/**
 * @module BriefingTile
 * @purpose L0 briefing tile — headline (AI or deterministic fallback), evidence chips, expand/collapse.
 * @depends types/reasoning, lib/deterministic-headline, react-i18next, lucide-react
 * @dependedOnBy BriefingBoardView
 *
 * @rules
 * - Performance: pure display — all data passed via props; no data fetching
 * - State: none — expand/collapse state owned by parent
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { composeDeterministicHeadline } from '../../../../../lib/deterministic-headline'
import { ReasoningClusterCard } from '../ReasoningClusterCard'
import { BriefingTileComparePanel } from './BriefingTileComparePanel'
import type { ClusterSummary, ResolvedObservation } from '../../../../../types/reasoning'

interface BriefingTileProps {
  cluster: ClusterSummary
  headline: string | undefined
  summary: string | undefined
  aiUnavailable: boolean
  expanded: boolean
  caseId: string
  runId: string
  onToggle: () => void
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

// Shared layout for the headline status badges; colour comes from a Tailwind class so each
// badge stays theme-correct (light/dark) — grey for the deterministic fallback, Evidenx lime
// for a real AI summary.
const BADGE_LAYOUT: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.56rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginLeft: '6px',
  verticalAlign: 'middle',
}

// Grey: deterministic / AI-unavailable fallback.
const MACHINE_BADGE_CLASS = 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
// Evidenx lime: a real AI-generated summary.
const AI_SUMMARY_BADGE_CLASS = 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300'

// Inline AI summary paragraph (the briefing's prose summary — shown only on AI tiles).
const SUMMARY_TEXT: React.CSSProperties = {
  margin: '6px 0 0',
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  lineHeight: 1.45,
  color: 'var(--text-dim)',
}

const RANK_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}

function EvidenceChips({ cluster }: { cluster: ClusterSummary }): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <>
      {cluster.hasPresenceEvidence && (
        <span style={CHIP_BASE}>{t('reasoning.briefingBoard.tile.evidence.presence')}</span>
      )}
      {cluster.hasReferenceEvidence && (
        <span style={CHIP_BASE}>{t('reasoning.briefingBoard.tile.evidence.reference')}</span>
      )}
      {cluster.hasExecutionEvidence && (
        <span style={CHIP_BASE}>{t('reasoning.briefingBoard.tile.evidence.execution')}</span>
      )}
    </>
  )
}

interface TileHeadlineProps {
  cluster: ClusterSummary
  headline: string | undefined
  aiUnavailable: boolean
}

function TileHeadline({ cluster, headline, aiUnavailable }: TileHeadlineProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const hasAiHeadline = headline !== undefined && !aiUnavailable

  if (hasAiHeadline) {
    return (
      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--text)' }}>
        {headline}
        <span style={BADGE_LAYOUT} className={AI_SUMMARY_BADGE_CLASS}>
          {t('reasoning.briefingBoard.tile.aiSummary')}
        </span>
      </span>
    )
  }

  const det = composeDeterministicHeadline(cluster)
  const flagKey = `reasoning.briefingBoard.tile.dominantFlag.${det.dominantFlag}` as const
  const headlineText = `${det.directorySegment} — ${t('reasoning.briefingBoard.tile.files', { count: det.fileCount })}; ${t(flagKey)}`

  return (
    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--text)' }}>
      {headlineText}
      <span style={BADGE_LAYOUT} className={MACHINE_BADGE_CLASS}>
        {aiUnavailable
          ? t('reasoning.briefingBoard.tile.aiUnavailable')
          : t('reasoning.briefingBoard.tile.machineGenerated')}
      </span>
    </span>
  )
}

function handleNoOp(_resolved: Map<string, ResolvedObservation>): void {
  /* drill-down disabled from board tile — cluster card has its own open-in-canvas button */
}

interface TileBodyProps {
  cluster: ClusterSummary
  headline: string | undefined
  summary: string | undefined
  aiUnavailable: boolean
  t: (key: string, opts?: Record<string, unknown>) => string
}

function TileBody({ cluster, headline, summary, aiUnavailable, t }: TileBodyProps): React.ReactElement {
  const hasAiHeadline = headline !== undefined && !aiUnavailable
  const showSummary = hasAiHeadline && summary !== undefined && summary.length > 0
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ marginBottom: '5px' }}>
        <TileHeadline cluster={cluster} headline={headline} aiUnavailable={aiUnavailable} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', alignItems: 'center' }}>
        <span style={RANK_CHIP}>{t('reasoning.clusterCard.rank', { position: cluster.rankPosition })}</span>
        <span style={CHIP_BASE}>{t('reasoning.briefingBoard.tile.files', { count: cluster.fileCount })}</span>
        {cluster.referencedByCount > 0 && (
          <span style={CHIP_BASE}>{t('reasoning.briefingBoard.tile.referencedBy', { count: cluster.referencedByCount })}</span>
        )}
        <EvidenceChips cluster={cluster} />
      </div>
      {showSummary && <p style={SUMMARY_TEXT}>{summary}</p>}
    </div>
  )
}

/**
 * L0 briefing tile with AI headline or deterministic fallback.
 *
 * @behaviour Expands inline to show the existing ReasoningClusterCard (L1) when toggled.
 *   When headline is undefined or aiUnavailable, renders a deterministic headline
 *   composed from B4 fields plus a "machine-generated" / "AI unavailable" badge.
 *   Keyboard-accessible via button with aria-expanded.
 * @errors n/a — purely presentational
 */
export function BriefingTile({
  cluster, headline, summary, aiUnavailable, expanded,
  caseId, runId, onToggle,
}: BriefingTileProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const ariaLabel = expanded
    ? t('reasoning.briefingBoard.tile.collapse')
    : t('reasoning.briefingBoard.tile.expand')

  return (
    <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-card)' }}>
      <button
        type="button" onClick={onToggle} aria-expanded={expanded} aria-label={ariaLabel}
        style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '8px' }}
      >
        {expanded
          ? <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
          : <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: 'var(--text-muted)' }} />}
        <TileBody cluster={cluster} headline={headline} summary={summary} aiUnavailable={aiUnavailable} t={t as (key: string, opts?: Record<string, unknown>) => string} />
      </button>
      {expanded && (
        <div style={{ padding: '0 16px 12px 16px' }}>
          <ReasoningClusterCard caseId={caseId} runId={runId} cluster={cluster} expanded={true} onToggleExpanded={onToggle} onDrillToCluster={handleNoOp} />
          <BriefingTileComparePanel caseId={caseId} runId={runId} clusterId={cluster.clusterId} />
        </div>
      )}
    </div>
  )
}
